import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const ScenarioLibrary = () => {
    const navigate = useNavigate();
    const [scenarioType, setScenarioType] = useState('standard'); // 'standard' | 'sca'
    const [scenarios, setScenarios] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    const fetchTags = useCallback(async () => {
        try {
            const res = await api.getTags();
            setTags(res.data.tags);
        } catch (err) {
            console.error("Failed to load tags", err);
        }
    }, []);

    const fetchScenarios = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (searchQuery) params.search = searchQuery;
            if (selectedTags.length > 0) params.tags = selectedTags.join(',');

            let res;
            if (scenarioType === 'sca') {
                res = await api.getSCAScenarios(params);
                // API returns array directly based on router check, but let's be safe if it changes
                const data = Array.isArray(res.data) ? res.data : (res.data.scenarios || []);
                setScenarios(data);
            } else {
                res = await api.getLibraryScenarios(params);
                setScenarios(res.data.scenarios);
            }
        } catch (err) {
            console.error("Failed to load scenarios", err);
            setScenarios([]);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, selectedTags, scenarioType]);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    useEffect(() => {
        fetchScenarios();
    }, [fetchScenarios]);

    const toggleTag = (tagId) => {
        setSelectedTags(prev =>
            prev.includes(tagId)
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        );
    };

    const groupedTags = tags.reduce((acc, tag) => {
        if (!acc[tag.category]) acc[tag.category] = [];
        acc[tag.category].push(tag);
        return acc;
    }, {});

    const stripHtml = (html) => {
        if (!html) return '';
        return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    };

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h1>Scenario Library</h1>

                <div style={{ display: 'flex', background: '#e9ecef', padding: '5px', borderRadius: '8px' }}>
                    <button
                        onClick={() => setScenarioType('standard')}
                        style={{
                            padding: '8px 20px',
                            border: 'none',
                            borderRadius: '6px',
                            background: scenarioType === 'standard' ? 'white' : 'transparent',
                            color: scenarioType === 'standard' ? '#0070f3' : '#666',
                            fontWeight: scenarioType === 'standard' ? 'bold' : 'normal',
                            boxShadow: scenarioType === 'standard' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer'
                        }}
                    >
                        Standard (RCA)
                    </button>
                    <button
                        onClick={() => setScenarioType('sca')}
                        style={{
                            padding: '8px 20px',
                            border: 'none',
                            borderRadius: '6px',
                            background: scenarioType === 'sca' ? 'white' : 'transparent',
                            color: scenarioType === 'sca' ? '#0070f3' : '#666',
                            fontWeight: scenarioType === 'sca' ? 'bold' : 'normal',
                            boxShadow: scenarioType === 'sca' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer'
                        }}
                    >
                        SCA (RCGP)
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => navigate(scenarioType === 'sca' ? '/sca' : '/teacher')}
                        style={{ padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Create New {scenarioType === 'sca' ? 'SCA' : 'Scenario'}
                    </button>
                </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    placeholder="Search scenarios..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-bg"
                    style={{ width: '100%', padding: '12px', fontSize: '16px', boxSizing: 'border-box', borderRadius: '4px' }}
                />
            </div>

            <div style={{ marginBottom: '30px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {Object.entries(groupedTags).map(([category, categoryTags]) => (
                    <div key={category} style={{ minWidth: '200px' }}>
                        <h4 style={{ textTransform: 'capitalize', margin: '0 0 10px 0' }}>{category}</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {categoryTags.map(tag => (
                                <button
                                    key={tag.id}
                                    onClick={() => toggleTag(tag.id)}
                                    className={selectedTags.includes(tag.id) ? '' : 'tag-default'}
                                    style={{
                                        padding: '5px 10px',
                                        borderRadius: '15px',
                                        border: '1px solid var(--border-color)',
                                        background: selectedTags.includes(tag.id) ? '#0070f3' : undefined,
                                        color: selectedTags.includes(tag.id) ? 'white' : undefined,
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                >
                                    {tag.name}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {loading ? (
                <p>Loading library...</p>
            ) : scenarios.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <h3>No scenarios found</h3>
                    <p>Try adjusting your search or filters.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {scenarios.map(scenario => (
                        <div key={scenario.id} className="card-bg" style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '20px', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <h3 style={{ marginTop: 0, fontSize: '1.2em' }}>{scenario.author_name || 'Community Scenario'}</h3>
                                {scenarioType === 'sca' && (
                                    <span style={{ fontSize: '0.8em', padding: '2px 6px', borderRadius: '4px', background: '#eee', color: '#555' }}>
                                        {scenario.difficulty || 'Intermediate'}
                                    </span>
                                )}
                            </div>

                            {scenarioType === 'sca' ? (
                                <>
                                    <div style={{ marginBottom: '10px', fontSize: '0.9em', color: '#0070f3', fontWeight: 'bold' }}>
                                        {scenario.clinical_experience_group}
                                    </div>
                                    <p style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        fontSize: '0.95em',
                                        color: '#444'
                                    }}>
                                        {stripHtml(scenario.candidate_material)}
                                    </p>
                                </>
                            ) : (
                                <p style={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical'
                                }}>
                                    {stripHtml(scenario.patient_details)}
                                </p>
                            )}

                            <div style={{ marginBottom: '15px', display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '10px' }}>
                                {(scenario.tags || []).slice(0, 3).map(tag => (
                                    <span key={tag.id} className="secondary-bg" style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                        {tag.name}
                                    </span>
                                ))}
                                {(scenario.tags || []).length > 3 && <span style={{ fontSize: '12px', color: '#666' }}>+{(scenario.tags || []).length - 3} more</span>}
                            </div>
                            <button
                                onClick={() => navigate(scenarioType === 'sca' ? `/sca?libraryId=${scenario.id}` : `/teacher?libraryId=${scenario.id}`)}
                                style={{
                                    padding: '10px',
                                    background: '#0070f3',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    width: '100%'
                                }}
                            >
                                Use This Practice Case
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ScenarioLibrary;
